import {
  ControlOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
  InfoCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  assignAnchors,
  type InputSubtype,
  type InputValidation,
  renderInstanceLabel,
  signatorySlug,
} from '@bmo/form-core';
import {
  Alert,
  Button,
  Card,
  Drawer,
  Input,
  Modal,
  Popconfirm,
  Select,
  Switch,
  Tooltip,
  Typography,
} from 'antd';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  type FileEntryCondition,
  type FormSection,
  type OneSpanConfig,
  type SignatoryFieldMappings,
  type SignatoryRole,
  type SignatureDocument,
  type SignatureField,
  type SignaturePlacement,
  type WorkflowStep,
  type WorkflowTemplate,
  workflowsApi,
} from '@/api/workflows';
import { ICON_BTN_PROPS } from '@/components/iconButton';
import { handleApiError } from '@/lib/handleApiError';
import { message } from '@/lib/message';

import { ADVISOR_SLUG } from './advisorRole';
import { ConditionsEditorModal } from './conditions/ConditionsEditorModal';
import { hasEligibleSourceFields } from './conditions/sourceFields';
import {
  collectFieldChoices,
  collectPdfEntries,
  collectSectionsByFieldId,
  describeField,
  EMAIL_FIELD_TYPES,
  FIXED_INSTANCE_OPTION_LIMIT,
  LANGUAGE_FIELD_TYPES,
  NAME_FIELD_TYPES,
  PHONE_FIELD_TYPES,
  repeatableSectionForMappings,
} from './oneSpanHelpers';

const DEFAULT_LABEL_EN = 'Signatory';
const DEFAULT_LABEL_FR = 'Signataire';

const { Text } = Typography;
const I18N = 'workflow.steps.oneSpan';
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

/**
 * Per-signer authoring drawer for the OneSpanESign step.
 *
 * Owns WHO signs — the roster, their contact-field mappings, their display names
 * and their gating conditions. WHERE they sign is authored on the signature
 * placement page, reached by the button in this drawer's footer: a signature is
 * a box dragged onto a rendered PDF page, which is not something a 640px drawer
 * can show. The placements still ride this drawer's draft so a save here cannot
 * erase them (see `PlacementDraft`).
 *
 * Layout:
 *   - Advisor card (singleton, pinned at top, no contact mapping — info comes
 *     from the BFF/JWT session at runtime).
 *   - Applicant cards below (1..N), each with 4 contact-field selects and a
 *     read-only count of where they sign.
 *
 * Slug derivation: the advisor's wire slug is hard-coded "advisor"; applicant
 * slugs are derived from array position as "applicant", "applicant_2", … so
 * the originator never types or sees them.
 */
type Props = {
  templateId: string;
  workflow: WorkflowTemplate;
  step: WorkflowStep | null;
  editable: boolean;
  onClose: () => void;
  onChanged: () => Promise<void>;
};

type ApplicantDraft = {
  _uid: string;
  /** Wire slug captured at load time (e.g. "applicant_2"). On save we
   *  PREFER this over a freshly-derived position slug so downstream
   *  consumers (OneSpan webhook callbacks, audit logs) don't rebind when
   *  applicants are deleted/reordered. Null for fresh applicants — they
   *  pick up the lowest free position-derived slug at save time. */
  persistedSlug: string | null;
  nameFieldId: string | null;
  lastNameFieldId: string | null;
  emailFieldId: string | null;
  phoneFieldId: string | null;
  /**
   * Optional RadioGroup / SingleSelect field whose answer sets this signer's
   * e-signature invitation language. Null (the norm) leaves them on the
   * application language — see `SignatoryFieldMappings.languageFieldId`.
   */
  languageFieldId: string | null;
  /**
   * Pins this signer to one instance of the repeatable section its mapped
   * fields live in, instead of auto-multiplying into one signer per
   * instance. Null (the default) is today's behavior — see
   * `SignatoryRole.fixedInstanceIndex`. Only ever shown/settable when one of
   * the four identity mappings above resolves into a repeatable section —
   * `languageFieldId` is deliberately not one of them.
   */
  fixedInstanceIndex: number | null;
  /** Originator-typed display name shown wherever this signer is rendered
   *  (e.g. the runtime preview tag). Defaults to "Signatory"/"Signataire"
   *  but is fully editable (e.g. "Co-borrower", "Guarantor"). */
  labelEn: string;
  labelFr: string;
  /** Gating conditions — does this signer get included in the signing
   *  package. Empty list = always included. */
  conditionCombinator: 'all' | 'any' | null;
  conditions: FileEntryCondition[];
  /**
   * Whether this party is expected to sign. False makes them an applicant
   * only: their contact details still reach BMO as `applicantInfo`, but they
   * get no signature locations and no entry in the signing package.
   *
   * `placements` below is kept even while this is false, so toggling it off
   * and back on before saving doesn't lose the boxes the originator already
   * placed — `buildWirePayload` is where they actually get dropped.
   */
  requiresSignature: boolean;
  placements: PlacementDraft[];
};

type AdvisorDraft = {
  placements: PlacementDraft[];
};

type PlacementDraft = {
  _uid: string;
  /** Wire `name`/`binding` captured at load time. Preserving them across
   *  saves means `sig_field_1` stays bound to the same logical placement
   *  even after rows are added/deleted/reordered — keeps OneSpan webhook
   *  callbacks stable. Null for fresh rows — they get the lowest free
   *  per-doc number at save time. */
  persistedName: string | null;
  persistedBinding: string | null;
  pdfEntryId: string | null;
  textAnchor: string;
  /**
   * Box geometry, date rendering, the signature's own show-conditions and
   * whether it collects initials rather than a signature — all authored on the
   * signature placement page and NOT editable here.
   *
   * Carried through the draft purely so it survives a drawer save. The drawer
   * writes the whole config, so a field it forgot to round-trip would be erased
   * — every box the originator placed, silently, the next time they renamed a
   * signatory. That is why these are on the draft at all despite nothing in this
   * component reading them.
   *
   * Note the per-SIGNATURE conditions here are a different thing from the
   * per-SIGNER conditions on `ApplicantDraft`, which this drawer does author:
   * one gates a single box, the other gates the whole party.
   */
  enPlacement: SignaturePlacement | null;
  frPlacement: SignaturePlacement | null;
  enDatePlacements: SignaturePlacement[];
  frDatePlacements: SignaturePlacement[];
  dateFormat: string | null;
  dateFontSize: number | null;
  conditions: FileEntryCondition[];
  conditionCombinator: 'all' | 'any' | null;
  isInitials: boolean;
  enInputPlacements: SignaturePlacement[];
  frInputPlacements: SignaturePlacement[];
  inputSubtype: InputSubtype | null;
  inputValidation: InputValidation | null;
};

export function OneSpanConfigDrawer({
  templateId,
  workflow,
  step,
  editable,
  onClose,
  onChanged,
}: Props) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isFr = i18n.language.startsWith('fr');
  const [advisor, setAdvisor] = useState<AdvisorDraft>({ placements: [] });
  const [applicants, setApplicants] = useState<ApplicantDraft[]>([]);
  const [saving, setSaving] = useState(false);
  // Index of the applicant whose Conditions modal is open. Local-only — the
  // condition draft lives on the applicant itself and is persisted with the
  // rest of the drawer on Save, same as placements/labels.
  const [conditionsTargetIdx, setConditionsTargetIdx] = useState<number | null>(null);

  // Reset draft state ONLY when the drawer opens against a different step. The
  // 20s editor-lock poll generates new step prop refs even with unchanged
  // contents; gating on step.id keeps the originator's edits intact between
  // background refreshes.
  const initializedForStepIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!step) {
      initializedForStepIdRef.current = null;
      return;
    }
    if (initializedForStepIdRef.current === step.id) return;
    initializedForStepIdRef.current = step.id;
    const { advisor: a, applicants: ap } = draftsFromConfig(step.oneSpanConfig);
    setAdvisor(a);
    setApplicants(ap);
  }, [step]);

  const pdfChoices = useMemo(() => collectPdfEntries(workflow, isFr), [workflow, isFr]);
  const nameFieldChoices = useMemo(
    () => collectFieldChoices(workflow, isFr, NAME_FIELD_TYPES),
    [workflow, isFr],
  );
  const emailFieldChoices = useMemo(
    () => collectFieldChoices(workflow, isFr, EMAIL_FIELD_TYPES),
    [workflow, isFr],
  );
  const phoneFieldChoices = useMemo(
    () => collectFieldChoices(workflow, isFr, PHONE_FIELD_TYPES),
    [workflow, isFr],
  );
  const languageFieldChoices = useMemo(
    () => collectFieldChoices(workflow, isFr, LANGUAGE_FIELD_TYPES),
    [workflow, isFr],
  );
  const sectionsByFieldId = useMemo(() => collectSectionsByFieldId(workflow), [workflow]);

  if (!step) return null;

  const stepTitle = (isFr ? step.titleFr : step.titleEn) || step.titleEn || step.titleFr;
  const validationError = validateDraft(advisor, applicants, pdfChoices, t);

  const handleSave = async () => {
    if (validationError) {
      message.error(validationError);
      return;
    }
    setSaving(true);
    try {
      const payload = buildWirePayload(advisor, applicants);
      await workflowsApi.setOneSpanConfig(templateId, step.id, payload);
      message.success(t(`${I18N}.saved`));
      await onChanged();
      onClose();
    } catch (err) {
      handleApiError(err, t, t(`${I18N}.saveFailed`));
    } finally {
      setSaving(false);
    }
  };

  // ---- Applicant handlers --------------------------------------------------
  // All mutators use the functional-updater form so rapid sequential updates
  // (double-click on Add, or onChange firing alongside a state-derived effect)
  // can't drop work by reading a stale closure snapshot.

  const addApplicant = () => {
    setApplicants((prev) => [
      ...prev,
      {
        _uid: newUid(),
        persistedSlug: null,
        nameFieldId: null,
        lastNameFieldId: null,
        emailFieldId: null,
        phoneFieldId: null,
        languageFieldId: null,
        fixedInstanceIndex: null,
        labelEn: DEFAULT_LABEL_EN,
        labelFr: DEFAULT_LABEL_FR,
        conditionCombinator: null,
        conditions: [],
        requiresSignature: true,
        placements: [],
      },
    ]);
  };

  const updateApplicant = (i: number, patch: Partial<ApplicantDraft>) => {
    setApplicants((prev) => prev.map((a, idx) => (idx === i ? { ...a, ...patch } : a)));
  };

  const deleteApplicant = (i: number) => {
    setApplicants((prev) => prev.filter((_, idx) => idx !== i));
  };

  /**
   * Save the roster, then leave for the placement page.
   *
   * The save is not optional. This drawer's draft lives in React memory only, so
   * navigating away with unsaved edits loses them — and the placement page reads
   * its signatory list straight from the persisted config, so an unsaved new
   * signatory would simply not be there to place a box for.
   */
  const handleManagePlacements = async () => {
    if (validationError) {
      message.error(validationError);
      return;
    }
    setSaving(true);
    try {
      await workflowsApi.setOneSpanConfig(
        templateId,
        step.id,
        buildWirePayload(advisor, applicants),
      );
      await onChanged();
      navigate(`/originator/workflows/${templateId}/signature-placement?step=${step.id}`);
    } catch (err) {
      handleApiError(err, t, t(`${I18N}.saveFailed`));
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={!!step}
      onClose={onClose}
      title={t(`${I18N}.drawerTitle`, { stepTitle })}
      width={640}
      destroyOnHidden
      footer={
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <div>
            {validationError && editable && (
              <Text type="warning" style={{ fontSize: 12 }}>
                {validationError}
              </Text>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {/* Saves first, then navigates — see handleManagePlacements. */}
            <Tooltip title={t(`${I18N}.managePlacementsHelp`)}>
              <Button
                icon={<EnvironmentOutlined />}
                onClick={handleManagePlacements}
                disabled={!editable || !!validationError || pdfChoices.length === 0}
                loading={saving}
              >
                {t(`${I18N}.managePlacements`)}
              </Button>
            </Tooltip>
            <Button onClick={onClose} disabled={saving}>
              {t(`${I18N}.cancel`)}
            </Button>
            <Button
              type="primary"
              onClick={handleSave}
              disabled={!editable || !!validationError}
              loading={saving}
            >
              {t(`${I18N}.save`)}
            </Button>
          </div>
        </div>
      }
    >
      {/* ---- Advisor (singleton, pinned at top) ---- */}

      <SignerSectionHeading
        title={t(`${I18N}.advisorTitle`)}
        help={t(`${I18N}.advisorAutoFilled`)}
      />

      <SignerCard
        kind="advisor"
        placements={advisor.placements}
        pdfChoices={pdfChoices}
        disabled={!editable}
      />

      <div style={{ height: 20 }} />

      {/* ---- Applicants (1..N) ---- */}

      <SignerSectionHeading
        title={t(`${I18N}.signatoriesTitle`)}
        help={t(`${I18N}.signatoriesHelp`)}
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {applicants.map((applicant, i) => (
          <SignerCard
            key={applicant._uid}
            kind="applicant"
            applicantNumber={i + 1}
            applicant={applicant}
            placements={applicant.placements}
            nameChoices={nameFieldChoices}
            emailChoices={emailFieldChoices}
            phoneChoices={phoneFieldChoices}
            languageChoices={languageFieldChoices}
            pdfChoices={pdfChoices}
            sectionsByFieldId={sectionsByFieldId}
            isFr={isFr}
            disabled={!editable}
            hasConditionablesFields={hasEligibleSourceFields(workflow.steps, {
              kind: 'step',
              ownStepId: step.id,
            })}
            onChangeApplicant={(patch) => updateApplicant(i, patch)}
            onDeleteApplicant={() => deleteApplicant(i)}
            onEditConditions={() => setConditionsTargetIdx(i)}
          />
        ))}
        <Button icon={<PlusOutlined />} onClick={addApplicant} disabled={!editable} type="dashed">
          {t(`${I18N}.addSignatory`)}
        </Button>
      </div>

      <ConditionsEditorModal
        open={conditionsTargetIdx !== null}
        title={t(`${I18N}.conditionModalTitle`)}
        intro={t(`${I18N}.conditionIntro`)}
        initialConditions={
          conditionsTargetIdx !== null ? applicants[conditionsTargetIdx]!.conditions : []
        }
        initialCombinator={
          conditionsTargetIdx !== null ? applicants[conditionsTargetIdx]!.conditionCombinator : null
        }
        allSteps={workflow.steps}
        scope={{ kind: 'step', ownStepId: step.id }}
        onCancel={() => setConditionsTargetIdx(null)}
        onSave={(conditions, combinator) => {
          if (conditionsTargetIdx === null) return;
          updateApplicant(conditionsTargetIdx, { conditions, conditionCombinator: combinator });
          setConditionsTargetIdx(null);
        }}
      />

      {pdfChoices.length === 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginTop: 16 }}
          message={t(`${I18N}.placementsNoPdfs`)}
        />
      )}
    </Drawer>
  );
}

// ---- Sub-components --------------------------------------------------------

function SignerSectionHeading({ title, help }: { title: string; help: string }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <Text strong style={{ fontSize: 14, display: 'block' }}>
        {title}
      </Text>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {help}
      </Text>
    </div>
  );
}

type SignerCardProps =
  | {
      kind: 'advisor';
      placements: PlacementDraft[];
      pdfChoices: ReturnType<typeof collectPdfEntries>;
      disabled: boolean;
    }
  | {
      kind: 'applicant';
      applicantNumber: number;
      applicant: ApplicantDraft;
      placements: PlacementDraft[];
      nameChoices: ReturnType<typeof collectFieldChoices>;
      emailChoices: ReturnType<typeof collectFieldChoices>;
      phoneChoices: ReturnType<typeof collectFieldChoices>;
      languageChoices: ReturnType<typeof collectFieldChoices>;
      pdfChoices: ReturnType<typeof collectPdfEntries>;
      sectionsByFieldId: Map<string, FormSection>;
      isFr: boolean;
      disabled: boolean;
      /** Whether any prior-step field exists for the conditions picker to
       *  offer — hides the Conditions button entirely when there's nothing
       *  the originator could ever pick (e.g. the OneSpanESign step is the
       *  workflow's very first step). */
      hasConditionablesFields: boolean;
      onChangeApplicant: (patch: Partial<ApplicantDraft>) => void;
      onDeleteApplicant: () => void;
      onEditConditions: () => void;
    };

function SignerCard(props: SignerCardProps) {
  const { t } = useTranslation();
  const isApplicant = props.kind === 'applicant';
  return (
    <Card
      size="small"
      style={{ background: '#fafafa', borderColor: '#e5e7eb' }}
      styles={{
        body: { padding: '20px 18px 18px' },
        header: { padding: '14px 18px', borderBottom: '1px solid #eef2f6', minHeight: 'auto' },
      }}
      title={
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {/* Advisor card has no order badge — wire signing order is variable
              (and the advisor is appended LAST in buildWirePayload), so a
              hard-coded "1" badge would mislead. Applicants get their
              position-derived badge. */}
          {isApplicant && (
            <OrderBadge value={props.applicantNumber} ariaLabel={t(`${I18N}.index`)} />
          )}
          <Text strong style={{ fontSize: 14 }}>
            {isApplicant ? t(`${I18N}.typeSignatory`) : t(`${I18N}.typeAdvisor`)}
          </Text>
        </div>
      }
      extra={
        isApplicant ? (
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            {props.applicant.conditions.length > 0 && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t(`${I18N}.conditionallyN`, { count: props.applicant.conditions.length })}
              </Text>
            )}
            {props.hasConditionablesFields && (
              <Tooltip
                title={t(
                  props.applicant.conditions.length > 0
                    ? `${I18N}.conditionEdit`
                    : `${I18N}.conditionAdd`,
                )}
              >
                <Button
                  {...ICON_BTN_PROPS}
                  aria-label={t(
                    props.applicant.conditions.length > 0
                      ? `${I18N}.conditionEdit`
                      : `${I18N}.conditionAdd`,
                  )}
                  icon={
                    <ControlOutlined
                      style={{
                        color: props.applicant.conditions.length > 0 ? '#0179C1' : undefined,
                      }}
                    />
                  }
                  disabled={props.disabled}
                  onClick={props.onEditConditions}
                />
              </Tooltip>
            )}
            <Popconfirm
              title={t(`${I18N}.deleteSignatoryConfirm`)}
              onConfirm={props.onDeleteApplicant}
              okButtonProps={{ danger: true }}
              disabled={props.disabled}
            >
              <Tooltip title={t(`${I18N}.deleteSignatory`)}>
                <Button
                  {...ICON_BTN_PROPS}
                  aria-label={t(`${I18N}.deleteSignatory`)}
                  danger
                  icon={<DeleteOutlined />}
                  disabled={props.disabled}
                />
              </Tooltip>
            </Popconfirm>
          </div>
        ) : null
      }
    >
      {isApplicant && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 18 }}>
          <DisplayNameRow
            labelEn={props.applicant.labelEn}
            labelFr={props.applicant.labelFr}
            disabled={props.disabled}
            onChange={(patch) => props.onChangeApplicant(patch)}
          />
          <FieldMappingRow
            label={t(`${I18N}.mapName`)}
            required
            value={props.applicant.nameFieldId}
            choices={props.nameChoices}
            disabled={props.disabled}
            onChange={(id) => props.onChangeApplicant({ nameFieldId: id })}
          />
          <FieldMappingRow
            label={t(`${I18N}.mapLastName`)}
            required={false}
            value={props.applicant.lastNameFieldId}
            choices={props.nameChoices}
            disabled={props.disabled}
            onChange={(id) => props.onChangeApplicant({ lastNameFieldId: id })}
          />
          <FieldMappingRow
            label={t(`${I18N}.mapEmail`)}
            required
            value={props.applicant.emailFieldId}
            choices={props.emailChoices}
            disabled={props.disabled}
            onChange={(id) => props.onChangeApplicant({ emailFieldId: id })}
          />
          <FieldMappingRow
            label={t(`${I18N}.mapPhone`)}
            required
            value={props.applicant.phoneFieldId}
            choices={props.phoneChoices}
            disabled={props.disabled}
            onChange={(id) => props.onChangeApplicant({ phoneFieldId: id })}
          />
          <LanguageMappingRow
            value={props.applicant.languageFieldId}
            choices={props.languageChoices}
            disabled={props.disabled}
            onChange={(id) => props.onChangeApplicant({ languageFieldId: id })}
          />
          <FixedInstanceRow
            applicant={props.applicant}
            sectionsByFieldId={props.sectionsByFieldId}
            isFr={props.isFr}
            disabled={props.disabled}
            onChangeApplicant={props.onChangeApplicant}
          />
          <SignatureExpectedRow
            requiresSignature={props.applicant.requiresSignature}
            placementCount={props.placements.length}
            disabled={props.disabled}
            onChange={(requiresSignature) => props.onChangeApplicant({ requiresSignature })}
          />
        </div>
      )}

      {/* Where this signer signs — read-only. Boxes are dragged onto the page
          itself on the signature placement page, reached from the footer.
          Hidden entirely for a signatory expecting no signatures: they have no
          locations to show and none to author. */}
      {(!isApplicant || props.applicant.requiresSignature) && (
        <>
          <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 6 }}>
            {t(`${I18N}.signLocationsLabel`)}
          </Text>
          <PlacementSummary placements={props.placements} />
        </>
      )}
    </Card>
  );
}

/**
 * "Expects signatures" — the switch that separates a signatory from a party who
 * is merely named on the application.
 *
 * `applicantInfo` on the save request is built from these same cards, and not
 * everyone it describes signs: a beneficiary or a secondary contact is a real
 * applicant BMO wants identified with nothing to put a pen to. Turning this off
 * keeps the contact mappings above (they are what populates `applicantInfo`) and
 * takes away only the signing half — the signatory stops being offered on the
 * placement page, loses any boxes already placed on save, and is left out of the
 * e-signature package entirely.
 *
 * The placement count is called out when turning it off would discard boxes,
 * because the card stops showing them the moment the switch flips and a silent
 * loss on Save is the one outcome the originator cannot undo.
 */
function SignatureExpectedRow({
  requiresSignature,
  placementCount,
  disabled,
  onChange,
}: {
  requiresSignature: boolean;
  placementCount: number;
  disabled: boolean;
  onChange: (requiresSignature: boolean) => void;
}) {
  const { t } = useTranslation();
  const uid = useId();
  return (
    <div>
      <FieldLabel htmlFor={uid} text={t(`${I18N}.requiresSignatureLabel`)}>
        <Tooltip title={t(`${I18N}.requiresSignatureHelp`)}>
          <InfoCircleOutlined style={{ color: '#94a3b8', fontSize: 12 }} />
        </Tooltip>
      </FieldLabel>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Switch
          id={uid}
          checked={requiresSignature}
          onChange={onChange}
          disabled={disabled}
          size="small"
        />
        <Text type="secondary" style={{ fontSize: 12 }}>
          {t(requiresSignature ? `${I18N}.requiresSignatureOn` : `${I18N}.requiresSignatureOff`)}
        </Text>
      </div>
      {!requiresSignature && placementCount > 0 && (
        <Text type="warning" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
          {t(`${I18N}.requiresSignatureDropsPlacements`, { count: placementCount })}
        </Text>
      )}
    </div>
  );
}

/**
 * "3 locations across 2 PDFs · 1 not placed in French".
 *
 * The French count is called out rather than folded into the total because it is
 * the one asymmetry that costs a signature: an application generates one
 * language, so a box placed only in English is simply absent from a French
 * package. The submit gate refuses it, but the originator should see it here
 * rather than discovering it at submit.
 */
function PlacementSummary({ placements }: { placements: PlacementDraft[] }) {
  const { t } = useTranslation();

  if (placements.length === 0) {
    return (
      <Text type="secondary" style={{ fontSize: 12 }}>
        {t(`${I18N}.noPlacements`)}
      </Text>
    );
  }

  const pdfCount = new Set(placements.map((p) => p.pdfEntryId).filter(Boolean)).size;
  const unpaired = placements.filter(
    (p) => Boolean(p.enPlacement) !== Boolean(p.frPlacement),
  ).length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {t(`${I18N}.placementSummary`, { count: placements.length, pdfs: pdfCount })}
      </Text>
      {unpaired > 0 && (
        <Text type="warning" style={{ fontSize: 12 }}>
          {t(`${I18N}.placementLanguageGap`, { count: unpaired })}
        </Text>
      )}
    </div>
  );
}

function OrderBadge({ value, ariaLabel }: { value: number; ariaLabel: string }) {
  return (
    <span
      aria-label={ariaLabel}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 32,
        height: 32,
        padding: '0 10px',
        borderRadius: 6,
        background: '#fff',
        border: '1px solid #d9d9d9',
        fontSize: 14,
        fontWeight: 600,
        color: '#475569',
        lineHeight: 1,
        boxSizing: 'border-box',
      }}
    >
      {value}
    </span>
  );
}

/**
 * A real <label htmlFor> for the drawer's hand-rolled rows (WCAG 1.3.1 / 4.1.2).
 * This drawer uses no Form.Item, so its controls had no programmatic label at
 * all — the visible text sat in a plain <div>, which a screen reader never
 * associates with the input.
 *
 * The asterisk is aria-hidden because `required` is carried programmatically by
 * aria-required on the control; announcing "star" adds noise without meaning.
 */
function FieldLabel({
  htmlFor,
  text,
  required = false,
  small = false,
  children,
}: {
  htmlFor: string;
  text: string;
  required?: boolean;
  /** The placement rows use a smaller, lighter label than the signer rows. */
  small?: boolean;
  /** Trailing adornment (e.g. a help tooltip) rendered inside the label row. */
  children?: React.ReactNode;
}) {
  return (
    <label
      htmlFor={htmlFor}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        fontSize: small ? 11 : 12,
        fontWeight: small ? undefined : 500,
        marginBottom: 4,
        color: small ? '#6b7280' : '#475569',
      }}
    >
      {text}
      {required && (
        <span aria-hidden="true" style={{ color: '#c81414' }}>
          *
        </span>
      )}
      {children}
    </label>
  );
}

function DisplayNameRow({
  labelEn,
  labelFr,
  disabled,
  onChange,
}: {
  labelEn: string;
  labelFr: string;
  disabled: boolean;
  onChange: (patch: { labelEn?: string; labelFr?: string }) => void;
}) {
  const { t } = useTranslation();
  // Rendered once per signer card, so the ids must be instance-unique.
  const uid = useId();
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
      <div>
        <FieldLabel htmlFor={`${uid}-en`} text={t(`${I18N}.displayNameEn`)} required />
        <Input
          id={`${uid}-en`}
          aria-required="true"
          value={labelEn}
          onChange={(e) => onChange({ labelEn: e.target.value })}
          disabled={disabled}
        />
      </div>
      <div>
        <FieldLabel htmlFor={`${uid}-fr`} text={t(`${I18N}.displayNameFr`)} />
        <Input
          id={`${uid}-fr`}
          value={labelFr}
          onChange={(e) => onChange({ labelFr: e.target.value })}
          disabled={disabled}
        />
      </div>
    </div>
  );
}

function FieldMappingRow({
  label,
  required,
  value,
  choices,
  disabled,
  onChange,
}: {
  label: string;
  required: boolean;
  value: string | null;
  choices: ReturnType<typeof collectFieldChoices>;
  disabled: boolean;
  onChange: (id: string | null) => void;
}) {
  const uid = useId();
  return (
    <div>
      <FieldLabel htmlFor={uid} text={label} required={required} />
      <FieldChoiceSelect
        id={uid}
        required={required}
        value={value}
        choices={choices}
        disabled={disabled}
        onChange={onChange}
      />
    </div>
  );
}

/**
 * The optional per-signer language mapping.
 *
 * Its own component rather than another {@link FieldMappingRow} because it is
 * the one mapping whose VALUE has a contract: the four contact slots hand BMO
 * whatever the client typed, but this one has to resolve to `en` or `fr` before
 * it means anything, and the originator authoring the radio is the only person
 * who can make that true. Hence the help affordance — a modal rather than the
 * tooltip the neighbouring rows use, because the rule needs more than a line.
 *
 * Never required, and never part of {@link validateDraft}: a workflow that does
 * not ask the client their language is the normal case, and those signers get
 * the application language exactly as they always have.
 */
function LanguageMappingRow({
  value,
  choices,
  disabled,
  onChange,
}: {
  value: string | null;
  choices: ReturnType<typeof collectFieldChoices>;
  disabled: boolean;
  onChange: (id: string | null) => void;
}) {
  const { t } = useTranslation();
  const uid = useId();
  const [helpOpen, setHelpOpen] = useState(false);
  return (
    <div>
      <FieldLabel htmlFor={uid} text={t(`${I18N}.mapLanguage`)}>
        <Button
          type="text"
          size="small"
          aria-label={t(`${I18N}.mapLanguageHelpTitle`)}
          onClick={() => setHelpOpen(true)}
          style={{ height: 16, width: 16, minWidth: 16, padding: 0, lineHeight: 1 }}
          icon={<InfoCircleOutlined style={{ color: '#94a3b8', fontSize: 12 }} />}
        />
      </FieldLabel>
      <FieldChoiceSelect
        id={uid}
        required={false}
        value={value}
        choices={choices}
        disabled={disabled}
        onChange={onChange}
      />
      <LanguageMappingHelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  );
}

/**
 * What the originator has to get right for the language mapping to do anything.
 *
 * The trap this exists to head off: the selected field's stored option VALUE is
 * what gets read, not its label. A radio labelled "English / Français" whose
 * values are "1" and "2" silently falls back to the application language, and
 * nothing at authoring time can detect that — the field is shared with the rest
 * of the form and is only language-shaped once it is mapped here.
 */
function LanguageMappingHelpModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={onClose}
      cancelButtonProps={{ style: { display: 'none' } }}
      okText={t('common.gotIt')}
      title={t(`${I18N}.mapLanguageHelpTitle`)}
    >
      <Text style={{ display: 'block', whiteSpace: 'pre-line' }}>
        {t(`${I18N}.mapLanguageHelpBody`)}
      </Text>
    </Modal>
  );
}

function FieldChoiceSelect({
  value,
  choices,
  onChange,
  disabled,
  id,
  required,
}: {
  value: string | null;
  choices: ReturnType<typeof collectFieldChoices>;
  onChange: (id: string | null) => void;
  disabled?: boolean;
  /** Links the control to its <label htmlFor>; AntD forwards this to the inner input. */
  id?: string;
  required?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Select
      id={id}
      aria-required={required ? 'true' : undefined}
      value={value ?? undefined}
      onChange={(v) => onChange(v ?? null)}
      placeholder={t(`${I18N}.selectField`)}
      style={{ width: '100%' }}
      allowClear
      showSearch
      optionFilterProp="label"
      notFoundContent={t(`${I18N}.selectFieldEmpty`)}
      options={choices.map((c) => ({ value: c.id, label: describeField(c) }))}
      disabled={disabled}
    />
  );
}

/**
 * "Signs as" — lets the originator pin this applicant card to one instance of
 * the repeatable section its contact mappings live in, instead of relying on
 * the auto-multiply behavior (one role auto-expanding into one signer per
 * instance) that only ever gets a single physical signature box. Renders
 * nothing when none of the four mappings resolve into a repeatable section —
 * the ordinary, non-repeatable case is unaffected.
 *
 * Option labels reuse `renderInstanceLabel` so "Account Holder 1" here reads
 * exactly the same as the section header the advisor sees at runtime.
 */
function FixedInstanceRow({
  applicant,
  sectionsByFieldId,
  isFr,
  disabled,
  onChangeApplicant,
}: {
  applicant: ApplicantDraft;
  sectionsByFieldId: Map<string, FormSection>;
  isFr: boolean;
  disabled: boolean;
  onChangeApplicant: (patch: Partial<ApplicantDraft>) => void;
}) {
  const { t } = useTranslation();
  const uid = useId();
  const section = repeatableSectionForMappings(applicant, sectionsByFieldId);
  if (!section) return null;

  const limit = section.maxInstances ?? FIXED_INSTANCE_OPTION_LIMIT;
  const options = [
    { value: 'auto', label: t(`${I18N}.fixedInstanceAuto`) },
    ...Array.from({ length: limit }, (_, i) => ({
      value: String(i),
      label: renderInstanceLabel(section, i, isFr ? 'fr' : 'en'),
    })),
  ];

  return (
    <div>
      <FieldLabel htmlFor={uid} text={t(`${I18N}.fixedInstanceLabel`)}>
        <Tooltip title={t(`${I18N}.fixedInstanceHelp`)}>
          <InfoCircleOutlined style={{ color: '#94a3b8', fontSize: 12 }} />
        </Tooltip>
      </FieldLabel>
      <Select
        id={uid}
        value={
          applicant.fixedInstanceIndex === null ? 'auto' : String(applicant.fixedInstanceIndex)
        }
        onChange={(v) => onChangeApplicant({ fixedInstanceIndex: v === 'auto' ? null : Number(v) })}
        style={{ width: '100%' }}
        options={options}
        disabled={disabled}
      />
    </div>
  );
}

// ---- Draft <-> wire shape conversions --------------------------------------

function draftsFromConfig(config: OneSpanConfig | null | undefined): {
  advisor: AdvisorDraft;
  applicants: ApplicantDraft[];
} {
  if (!config) return { advisor: { placements: [] }, applicants: [] };

  // Split signers by type. There's exactly one advisor by design; everything
  // else is an applicant.
  const advisorRole = config.roles.find((r) => r.type === 'advisor');
  const applicantRoles = config.roles.filter((r) => r.type === 'applicant');

  // For each role, collect placements (pdfEntryId + anchor) by walking the
  // documents tree once and grouping by role slug. This is the inverse of
  // buildWirePayload's "group placements by pdf" step.
  // Per-placement `name`/`binding` are also captured so we can preserve the
  // wire identity across saves (sig_field_1 stays bound to the same logical
  // placement even after rows change).
  const placementsBySlug = new Map<string, PlacementDraft[]>();
  for (const doc of config.documents) {
    for (const f of doc.fields) {
      const list = placementsBySlug.get(f.role) ?? [];
      list.push({
        _uid: newUid(),
        persistedName: f.name,
        persistedBinding: f.binding,
        pdfEntryId: doc.pdfEntryId,
        textAnchor: f.textAnchor,
        // Read and written back untouched — see PlacementDraft.
        enPlacement: f.enPlacement ?? null,
        frPlacement: f.frPlacement ?? null,
        enDatePlacements: f.enDatePlacements ?? [],
        frDatePlacements: f.frDatePlacements ?? [],
        dateFormat: f.dateFormat ?? null,
        dateFontSize: f.dateFontSize ?? null,
        conditions: f.conditions ?? [],
        conditionCombinator: f.conditionCombinator ?? null,
        isInitials: f.isInitials ?? false,
        enInputPlacements: f.enInputPlacements ?? [],
        frInputPlacements: f.frInputPlacements ?? [],
        inputSubtype: f.inputSubtype ?? null,
        inputValidation: f.inputValidation ?? null,
      });
      placementsBySlug.set(f.role, list);
    }
  }

  // `null` is in the union for `languageFieldId`, which is optional on the wire
  // type rather than an always-present Guid like the four identity slots.
  const unset = (id: string | null | undefined) => (!id || id === EMPTY_GUID ? null : id);

  const advisor: AdvisorDraft = {
    placements: advisorRole ? (placementsBySlug.get(advisorRole.slug) ?? []) : [],
  };
  const applicants: ApplicantDraft[] = applicantRoles.map((r) => ({
    _uid: newUid(),
    persistedSlug: r.slug,
    nameFieldId: unset(r.fieldMappings?.nameFieldId),
    lastNameFieldId: unset(r.fieldMappings?.lastNameFieldId),
    emailFieldId: unset(r.fieldMappings?.emailFieldId),
    phoneFieldId: unset(r.fieldMappings?.phoneFieldId),
    // Absent on every role authored before the language mapping existed, which
    // `unset` already reads as "not mapped".
    languageFieldId: unset(r.fieldMappings?.languageFieldId),
    fixedInstanceIndex: r.fixedInstanceIndex ?? null,
    // Legacy roles saved before this field existed fall back to the default
    // display name rather than showing a blank input.
    labelEn: r.labelEn ?? DEFAULT_LABEL_EN,
    labelFr: r.labelFr ?? DEFAULT_LABEL_FR,
    conditionCombinator: r.conditionCombinator ?? null,
    conditions: r.conditions ?? [],
    // Absent on every role authored before the toggle existed, and those all sign.
    requiresSignature: r.requiresSignature !== false,
    placements: placementsBySlug.get(r.slug) ?? [],
  }));
  return { advisor, applicants };
}

function buildWirePayload(advisor: AdvisorDraft, applicants: ApplicantDraft[]): OneSpanConfig {
  // Stable applicant slug derivation: prefer the persisted slug captured at
  // load time so downstream consumers (OneSpan webhooks, audit logs) don't
  // rebind when the originator deletes/reorders applicants. Fresh applicants
  // pick up the lowest free position-derived slug.
  const usedSlugs = new Set<string>(
    applicants.map((a) => a.persistedSlug).filter((s): s is string => s !== null),
  );
  const nextFreeSlug = () => {
    for (let n = 1; ; n++) {
      const candidate = n === 1 ? 'applicant' : `applicant_${n}`;
      if (!usedSlugs.has(candidate)) {
        usedSlugs.add(candidate);
        return candidate;
      }
    }
  };
  const applicantSlugs = applicants.map((a) => a.persistedSlug ?? nextFreeSlug());

  // Build roles in two passes so applicants take the leading indexes and
  // the advisor lands at the end ONLY when there are advisor placements —
  // no phantom advisor for an unused signer.
  let order = 1;
  const roles: SignatoryRole[] = applicants.map((a, i) => ({
    slug: applicantSlugs[i]!,
    type: 'applicant',
    index: order++,
    fieldMappings: {
      nameFieldId: a.nameFieldId ?? EMPTY_GUID,
      lastNameFieldId: a.lastNameFieldId ?? EMPTY_GUID,
      emailFieldId: a.emailFieldId ?? EMPTY_GUID,
      phoneFieldId: a.phoneFieldId ?? EMPTY_GUID,
      languageFieldId: a.languageFieldId ?? EMPTY_GUID,
    } as SignatoryFieldMappings,
    labelEn: a.labelEn.trim() || DEFAULT_LABEL_EN,
    labelFr: a.labelFr.trim(),
    conditionCombinator: a.conditions.length > 0 ? a.conditionCombinator : null,
    conditions: a.conditions,
    fixedInstanceIndex: a.fixedInstanceIndex,
    requiresSignature: a.requiresSignature,
  }));
  if (advisor.placements.some((p) => p.pdfEntryId)) {
    roles.push({
      slug: ADVISOR_SLUG,
      type: 'advisor',
      index: order++,
      fieldMappings: null,
      labelEn: null,
      labelFr: null,
    });
  }

  // Group every placement by pdfEntryId. Skip rows missing a PDF (the
  // validator already blocked the save if there were partial rows we
  // couldn't drop). Preserve each placement's stable `name`/`binding` when
  // present; assign the lowest free per-doc number to fresh rows.
  type WireField = SignatureField;
  const byPdf = new Map<string, { used: Set<number>; fields: (WireField | null)[] }>();

  // First pass: place rows that carry a persisted name/binding at the index
  // their suffix implies (e.g. sig_field_3 → slot 2). Reserves their numbers
  // so subsequent fresh rows fill the gaps without colliding.
  const parseSuffix = (s: string): number => {
    const m = /(\d+)$/.exec(s);
    return m ? parseInt(m[1]!, 10) : NaN;
  };
  type Pending = { slug: string; p: PlacementDraft };
  const pendingByPdf = new Map<string, Pending[]>();

  const enqueue = (slug: string, placements: PlacementDraft[]) => {
    for (const p of placements) {
      if (!p.pdfEntryId) continue;
      const arr = pendingByPdf.get(p.pdfEntryId) ?? [];
      arr.push({ slug, p });
      pendingByPdf.set(p.pdfEntryId, arr);
    }
  };
  // A signatory switched to "no signatures" drops every box it had: the domain refuses a
  // placement whose role does not sign, and a box the originator can no longer see or edit
  // (the card hides the list) is one they cannot fix either. Dropping them HERE rather than
  // on toggle is what lets the switch be flipped back before Save with nothing lost.
  applicants.forEach((a, i) => {
    if (a.requiresSignature) enqueue(applicantSlugs[i]!, a.placements);
  });
  enqueue(ADVISOR_SLUG, advisor.placements);

  for (const [pdfEntryId, pending] of pendingByPdf) {
    const used = new Set<number>();
    const slots: (WireField | null)[] = [];
    // Pass 1 — placements with persisted name keep their slot.
    for (const { slug, p } of pending) {
      if (!p.persistedName) continue;
      const n = parseSuffix(p.persistedName);
      if (!Number.isFinite(n) || n < 1 || used.has(n)) continue;
      used.add(n);
      while (slots.length < n) slots.push(null);
      slots[n - 1] = {
        name: p.persistedName,
        binding: p.persistedBinding ?? `field_${n}`,
        textAnchor: p.textAnchor.trim(),
        role: slug,
        ...carriedOf(p),
      };
    }
    // Pass 2 — fresh placements fill the lowest free numbers.
    let cursor = 1;
    const nextFree = (): number => {
      while (used.has(cursor)) cursor++;
      used.add(cursor);
      return cursor;
    };
    for (const { slug, p } of pending) {
      if (p.persistedName && Number.isFinite(parseSuffix(p.persistedName))) continue;
      const n = nextFree();
      while (slots.length < n) slots.push(null);
      slots[n - 1] = {
        name: `sig_field_${n}`,
        binding: `field_${n}`,
        textAnchor: p.textAnchor.trim(),
        role: slug,
        ...carriedOf(p),
      };
    }
    byPdf.set(pdfEntryId, { used, fields: slots });
  }

  const documents: SignatureDocument[] = Array.from(byPdf.entries()).map(
    ([pdfEntryId, { fields }]) => ({
      pdfEntryId,
      // Filter out unfilled slots (impossible by construction but defensive).
      fields: fields.filter((f): f is WireField => f !== null),
    }),
  );

  // Anchors are derived from the current display names, never carried forward.
  // Renaming a signatory has to regenerate them, and it is safe to: the platform
  // stamps the white text at generation time, so there is no already-tagged PDF
  // for a regenerated anchor to fall out of step with. The placement page runs
  // the same function before its own saves.
  return { roles, documents: assignAnchors(roles, documents) };
}

/**
 * Every member of a signature this drawer does not author — its placement in each
 * language, its companion date and input boxes with their settings, its
 * show-conditions, and whether it collects initials — written back exactly as it
 * was read.
 *
 * Anything owned by the placement page belongs here: the drawer PUTs the whole
 * config, so a member missing from this projection is erased from every
 * signature the next time somebody saves the drawer.
 */
function carriedOf(
  p: PlacementDraft,
): Pick<
  SignatureField,
  | 'enPlacement'
  | 'frPlacement'
  | 'enDatePlacements'
  | 'frDatePlacements'
  | 'dateFormat'
  | 'dateFontSize'
  | 'conditions'
  | 'conditionCombinator'
  | 'isInitials'
  | 'enInputPlacements'
  | 'frInputPlacements'
  | 'inputSubtype'
  | 'inputValidation'
> {
  return {
    enPlacement: p.enPlacement,
    frPlacement: p.frPlacement,
    enDatePlacements: p.enDatePlacements,
    frDatePlacements: p.frDatePlacements,
    dateFormat: p.dateFormat,
    dateFontSize: p.dateFontSize,
    conditions: p.conditions,
    conditionCombinator: p.conditionCombinator,
    isInitials: p.isInitials,
    enInputPlacements: p.enInputPlacements,
    frInputPlacements: p.frInputPlacements,
    inputSubtype: p.inputSubtype,
    inputValidation: p.inputValidation,
  };
}

function validateDraft(
  advisor: AdvisorDraft,
  applicants: ApplicantDraft[],
  pdfChoices: ReadonlyArray<{ id: string }>,
  t: (key: string) => string,
): string | null {
  // Required mappings: name, email, phone must all be picked on every
  // applicant. Last name stays optional (many BMO forms collect full-name
  // in one field). Asterisks in the UI now match this rule.
  for (const a of applicants) {
    if (!a.labelEn.trim()) return t(`${I18N}.validation.signatoryLabelRequired`);
    if (!a.nameFieldId) return t(`${I18N}.validation.signatoryNameRequired`);
    if (!a.emailFieldId) return t(`${I18N}.validation.signatoryEmailRequired`);
    if (!a.phoneFieldId) return t(`${I18N}.validation.signatoryPhoneRequired`);
    // The anchor is built from the display name stripped to alphanumerics, so a
    // name with none of those has nothing to build from.
    if (!signatorySlug(a.labelEn)) return t(`${I18N}.validation.signatoryLabelUnnameable`);
  }

  // Display names deliberately do NOT have to be distinct — two parties on one
  // application are routinely both "Subscriber", and the form is BMO's wording,
  // not ours to disambiguate. Anchors still must be unique within a PDF, which
  // `anchorNamesBySlug` handles by numbering the second holder of a name apart
  // (`Subscriber` / `Subscriber2`) rather than by refusing the save.

  // Only the placements that will actually be written — a non-signing signatory's boxes are
  // dropped by `buildWirePayload`, so blocking the save over one pointing at a deleted PDF
  // would refuse a config whose offending row is already on its way out.
  const allPlacements = [
    ...advisor.placements,
    ...applicants.filter((a) => a.requiresSignature).flatMap((a) => a.placements),
  ];
  const knownPdfIds = new Set(pdfChoices.map((c) => c.id));
  for (const p of allPlacements) {
    // Placements are authored on the placement page against a PDF that was in
    // the picker at the time, so neither of these should be reachable through
    // the UI. They stay because a PDF entry can be DELETED afterwards, and a
    // placement pointing at a document that no longer exists would be saved,
    // then silently dropped when the payload is built.
    if (!p.pdfEntryId) return t(`${I18N}.validation.pdfRequired`);
    if (!knownPdfIds.has(p.pdfEntryId)) return t(`${I18N}.validation.pdfMissing`);
  }
  return null;
}

// ---- Misc helpers ----------------------------------------------------------

function newUid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `uid-${Math.random().toString(36).slice(2)}-${Date.now()}`;
}
